import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Heart, Loader2, ThumbsUp, Sparkles, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import { Button } from '@/components/ui';
import { useNames } from '../hooks/useNames';
import { useSuggestions } from '../hooks/useSuggestions';
import { SwipeableStack } from '../components/SwipeableStack';
import { NameFilters } from '../components/NameFilters';
import { MatchNotification } from '../components/MatchNotification';
import { SuggestNameModal } from '../components/SuggestNameModal';
import { NameLimitPaywall } from '../components/NameLimitPaywall';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';

export function NameSwipePage() {
  const { t } = useTranslation('names');
  const isNameSwipeEnabled = useFeatureEnabled('feature_name_swipe');
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const {
    currentName,
    nextName,
    hasMoreNames,
    hasMoreFromServer,
    isLoading,
    error,
    filters,
    newMatch,
    handleSwipe,
    clearMatch,
    updateFilters,
    resetFilters,
    refetch,
    // Limit-Info aus Phase 2
    hasUnlimitedSwipes,
    isLimitReached,
    remainingSwipes,
    dailySwipeCount,
    resetsAt,
    limitBlocked,
    hasSwipeStatus,
  } = useNames();
  const { pendingCount } = useSuggestions();

  // Der Server hat einen Swipe abgelehnt (z. B. weil der lokale Stand
  // veraltet war) - dann ebenfalls die Paywall zeigen.
  useEffect(() => {
    if (limitBlocked) setShowLimitModal(true);
  }, [limitBlocked]);

  const handleUpgrade = useCallback(() => {
    setShowLimitModal(false);
    navigate('/subscription');
  }, [navigate]);

  // Hinweis nur, wenn fuer heute Schluss ist. Solange noch Swipes uebrig
  // sind, bleibt der Platz ueber dem Stapel leer.
  const showCounter = hasSwipeStatus && !hasUnlimitedSwipes && remainingSwipes <= 0;

  // Show disabled message if feature is turned off
  if (!isNameSwipeEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Fehler beim Laden: sichtbar machen statt nur in der Konsole
  if (error && !currentName) {
    return (
      <>
        <h1 className="text-white text-2xl font-semibold mb-4">{t('pageTitle')}</h1>
        <div className="flex-1 flex items-center justify-center px-6 pt-16">
          <div className="text-center p-6 max-w-sm glass-effect rounded-2xl">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-white/60" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{t('error.title')}</h3>
            <p className="text-white/70 mb-6">{t('error.description')}</p>
            <Button onClick={() => refetch()} className="w-full bg-pp-orange hover:bg-pp-orange/90">
              {t('error.retry')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-2xl font-semibold mb-4">{t('pageTitle')}</h1>

      {/* Filter Row */}
      <div className="flex items-center gap-4 mb-4">
        {/* Scrollable Filter Tabs */}
        <div className="filter-tabs flex-1">
          <Link
            to="/tools/names/matches"
            className="filter-tab"
          >
            <Heart className="w-4 h-4" />
            {t('tabs.matches')}
          </Link>
          <Link
            to="/tools/names/likes"
            className="filter-tab"
          >
            <ThumbsUp className="w-4 h-4" />
            {t('tabs.likes')}
          </Link>
          <Link
            to="/tools/names/suggestions"
            className={cn(
              'filter-tab',
              pendingCount > 0 && 'relative'
            )}
          >
            <Sparkles className="w-4 h-4" />
            {t('tabs.suggestions')}
            {pendingCount > 0 && (
              <span className="ml-1 bg-white/30 px-1.5 py-0.5 rounded text-xs">
                {pendingCount}
              </span>
            )}
          </Link>
        </div>

        {/* Filter Button */}
        <button
          onClick={() => setShowFilters(true)}
          className="bg-pp-orange rounded-[5px] p-1.5 flex-shrink-0"
        >
          <SlidersHorizontal className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Hinweis, wenn fuer heute Schluss ist */}
      {showCounter && (
        <p className="text-white/70 text-sm mb-2 text-center">
          {t('counter.none')}
        </p>
      )}

      {/* Swipeable Stack */}
      <SwipeableStack
        currentName={currentName}
        nextName={nextName}
        onSwipe={handleSwipe}
        hasMoreNames={hasMoreNames}
        isLoadingMore={hasMoreFromServer}
        isLimitReached={isLimitReached}
        onLimitReached={() => setShowLimitModal(true)}
        onOpenFilters={() => setShowFilters(true)}
        onSuggestName={() => setShowSuggestModal(true)}
      />

      {/* Filters Drawer */}
      {showFilters && (
        <NameFilters
          filters={filters}
          onUpdate={updateFilters}
          onReset={resetFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Suggest Name Modal */}
      <SuggestNameModal
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
      />

      {/* Tageslimit erreicht */}
      <NameLimitPaywall
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        swipesUsed={dailySwipeCount}
        resetsAt={resetsAt}
        onUpgrade={handleUpgrade}
      />

      {/* Match Notification */}
      {newMatch && (
        <MatchNotification
          match={newMatch}
          onClose={clearMatch}
          onViewMatches={() => {
            clearMatch();
            navigate('/tools/names/matches');
          }}
        />
      )}
    </>
  );
}
